// eslint-disable @typescript-eslint/no-non-null-assertion
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Table, TableRowExpandEvent } from 'primeng/table';
import { environment } from 'src/environments/environment';
import { SortDirectionShared } from '../../../models/interface/sortDirection.enum';
import { TableDataListShared } from '../../../models/interface/tableDataList.interface';
import { Filters } from '../../../types/filters.type';
import { Flags } from '../../../types/flags.type';
import { TableColumn } from '../../../types/tableColumn.type';
import { MuTableCaptionDirective } from '../directives/mu-table-caption.directive';
import { MuTableCellEditorDirective } from '../directives/mu-table-cell-editor.directive';
import { MuTableCellDirective } from '../directives/mu-table-cell.directive';
import { MuTableFilterDirective } from '../directives/mu-table-filter.directive';
import { MuTableNotFoundDirective } from '../directives/mu-table-not-found.directive';
import { TableSizes } from '../models/tableSizes.interface';
import { ExpandedTableDataListShared } from '../../../models/interface/expandedDataLit.interface';
import { MuAccordionPreviewTemplateDirective } from '../directives/mu-accordion-preview-template.directive';

@Component({
  selector: 'app-mu-table',
  templateUrl: './mu-table.component.html',
  styleUrls: ['./mu-table.component.sass'],
})
export class MuTableComponent<FlagsEnum, Data, DataFilters, SortIdList>
  implements OnInit, AfterViewInit
{
  @Input() id: string;
  @Input() columns: TableColumn<Data, DataFilters, SortIdList>[];
  @Input() responsive: boolean;
  @Input() flags: Flags<FlagsEnum>;
  @Input() flagEmpty: FlagsEnum;
  @Input() dataList: TableDataListShared<Data>;
  @Input() paginator: boolean;
  @Input() filters: Filters<DataFilters>;
  @Input() bodyTrTarget: string;
  @Input() contextLang: string;
  @Input() showCustomNotFound: boolean;
  @Input() paginatorCancelRedirect: string;
  @Input() paginatorButtonLoading: boolean;
  @Input() paginatorDisableButton: boolean;
  @Input() scrollable: boolean;
  @Input() rowsPerPageOptions: any[] = [5, 10, 25, 50];
  @Input() rowSelectable = false;
  @Input() customNotFoundClass = '';
  @Input() tableStyle: {};
  @Input() dataKey: string = 'muIndex';
  @Input() tableSizes: TableSizes = {
    mobileLabel: 3,
    mobileValue: 9,
    tabletLabel: 3,
    tabletValue: 9,
  };
  @Input() enableButtons?: boolean = false;
  @ContentChildren(MuTableCaptionDirective) captionTemplateList: QueryList<MuTableCaptionDirective>;
  @ContentChildren(MuTableFilterDirective) filterTemplateList: QueryList<MuTableFilterDirective>;
  @ContentChildren(MuTableCellDirective) cellTemplateList: QueryList<MuTableCellDirective>;
  @ContentChildren(MuTableCellEditorDirective)
  cellTemplateEditorList: QueryList<MuTableCellEditorDirective>;
  @ContentChildren(MuTableNotFoundDirective)
  notFoundTemplateList: QueryList<MuTableNotFoundDirective>;
  @ContentChildren(MuAccordionPreviewTemplateDirective)
  accordionTemplateList: QueryList<MuAccordionPreviewTemplateDirective>;
  @ViewChild('defaultCell', { read: TemplateRef }) defaultCellTemplate: TemplateRef<any>;
  @ViewChild('defaultFilter', { read: TemplateRef }) defaultFilterTemplate: TemplateRef<any>;
  @ViewChild('defaultCellEditor', { read: TemplateRef })
  defaultCellEditorTemplate: TemplateRef<any>;
  @ViewChild(Table) primeTable: Table;
  @Output() eventBodyTr = new EventEmitter();
  @Output() eventPaginatorAction = new EventEmitter();
  @Output() eventExpandedRowKeys = new EventEmitter();

  captionTemplate: TemplateRef<any>;
  accordionTemplate: TemplateRef<any>;
  notFoundTemplate: TemplateRef<any>;
  filterColumnsDefinition: TableColumn<Data, DataFilters, SortIdList>[] = [];
  editable: boolean;
  currentSortBy: SortIdList;
  sortDirection: SortDirectionShared;
  sortDirectionEnum = SortDirectionShared;
  tableEditor: TableEditor<FlagsEnum, Data, DataFilters, SortIdList>;
  currentRowSelected: HTMLElement;
  extraTableClasses = '';
  expandedRowKeys: ExpandedTableDataListShared = {};

  constructor(private changeDetectorRef: ChangeDetectorRef, private elementRef: ElementRef) {
    changeDetectorRef.detach();
  }

  ngOnInit(): void {
    /** */
  }

  ngAfterViewInit(): void {
    this.changeDetectorRef.detectChanges();
    this.tableEditor = new TableEditor(this, this.primeTable, this.elementRef);
    this.loadFilterColumnsDefinition();
    this.loadTemplates();
    this.editable = this.columns.find(column => column.editable) ? true : false;
    setTimeout(() => this.changeDetectorRef.reattach());
  }

  /**
   * method to get filter columns definition
   */
  loadFilterColumnsDefinition(): void {
    this.filterColumnsDefinition = this.columns.filter(column => column.withFilter);
  }

  onRowExpand(event: any) {
    this.expandedRowKeys = {};
    this.expandedRowKeys[event.data.id] = true;
    this.eventExpandedRowKeys.emit({ event, expanded: this.expandedRowKeys });
  }
  onRowCollapse(event: any) {
    this.eventExpandedRowKeys.emit({ event, expanded: this.expandedRowKeys });
  }
  /**
   * load templates
   */
  loadTemplates(): void {
    this.captionTemplate =
      this.captionTemplateList.length > 0 ? this.captionTemplateList.first.template : null;

    if (!this.captionTemplate) {
      this.extraTableClasses += ' p-table-caption-hidden ';
    }
    this.notFoundTemplate =
      this.notFoundTemplateList.length > 0 ? this.notFoundTemplateList.first.template : null;
    this.accordionTemplate =
      this.accordionTemplateList.length > 0 ? this.accordionTemplateList.first.template : null;
    const filterTemplateMap: { [key: string]: TemplateRef<any> } = {};
    const cellTemplateMap: { [key: string]: TemplateRef<any> } = {};
    const cellEditorTemplateMap: { [key: string]: TemplateRef<any> } = {};

    this.filterTemplateList.forEach(element => {
      filterTemplateMap[element.name || ''] = element.template;
    });

    this.cellTemplateList.forEach(element => {
      cellTemplateMap[element.name || ''] = element.template;
    });

    this.cellTemplateEditorList.forEach(element => {
      cellEditorTemplateMap[element.name || ''] = element.template;
    });

    if (!cellTemplateMap['']) {
      cellTemplateMap[''] = this.defaultCellTemplate;
    }
    if (!filterTemplateMap['']) {
      filterTemplateMap[''] = this.defaultFilterTemplate;
    }
    if (!cellEditorTemplateMap['']) {
      cellEditorTemplateMap[''] = this.defaultCellEditorTemplate;
    }

    this.loadColumns(filterTemplateMap, cellEditorTemplateMap, cellTemplateMap);
  }

  /**
   * laod columns
   * @param {[key: string]: TempalteRef<any>} filterTemplateMap
   * @param {[key: string]: TempalteRef<any>} cellEditorTemplateMap
   * @param {[key: string]: TempalteRef<any>} cellTemplateMap
   */
  loadColumns(
    filterTemplateMap: { [key: string]: TemplateRef<any> },
    cellEditorTemplateMap: { [key: string]: TemplateRef<any> },
    cellTemplateMap: { [key: string]: TemplateRef<any> }
  ): void {
    this.columns.forEach(element => {
      if (element.withFilter) {
        element.filterTemplate =
          filterTemplateMap[element.filter as string] || filterTemplateMap[''];
      }
      if (element.editable) {
        element.editorTemplate =
          cellEditorTemplateMap[(element.key || element.label) as string] ||
          cellEditorTemplateMap[''];
      }
      element.valueTemplate =
        cellTemplateMap[(element.key || element.label) as string] || cellTemplateMap[''];
    });
  }

  get deviceLabelClassName() {
    return `p-col-${this.tableSizes?.mobileLabel || 3} p-md-${this.tableSizes?.tabletLabel || 3}`;
  }

  get deviceValueClassName() {
    return `p-col-${this.tableSizes?.mobileValue || 9} p-md-${this.tableSizes?.tabletValue || 9}`;
  }

  /**
   * Call when the user move through the pages or change the number of items by page
   */
  pageChanged(event: any): void {
    const currentLimit = (this.dataList as any).limit;
    const newLimit = event.rows;
    if (currentLimit !== newLimit) {
      (this.dataList as any).page = 1;
      this.filters.emitFilterValue('limit' as keyof DataFilters, event.rows);
      return environment.guardClause;
    }
    this.filters.emitFilterValue('page' as keyof DataFilters, event.page + 1);
  }

  /**
   * emit event tbody tr click
   */
  _eventBodyTr(event: Event, value: any): void {
    if (this.rowSelectable) {
      const row = event.currentTarget as HTMLElement;
      if (this.currentRowSelected) {
        this.currentRowSelected.classList.remove('mu-table-row-selected');
      }
      this.currentRowSelected = row;
      row.classList.add('mu-table-row-selected');
    }
    this.eventBodyTr.emit({ element: event, value });
  }

  /**
   * sort emit event
   *
   * @param column
   */
  sort(column: TableColumn<Data, DataFilters, SortIdList>): void {
    if (!column.sortable) {
      return environment.guardClause;
    }
    const isDesc =
      this.sortDirection === SortDirectionShared.ASC
        ? SortDirectionShared.DESC
        : SortDirectionShared.ASC;
    this.sortDirection = this.currentSortBy !== column.sortable ? SortDirectionShared.ASC : isDesc;
    this.currentSortBy = column.sortable;
    this.filters.emitFilterValue(
      'sortBy' as keyof DataFilters,
      column.sortResetOnDesc && this.sortDirection === SortDirectionShared.DESC
        ? null
        : this.currentSortBy
    );
    this.filters.emitFilterValue(
      'sortDirection' as keyof DataFilters,
      column.sortResetOnDesc && this.sortDirection === SortDirectionShared.DESC
        ? SortDirectionShared.ASC
        : this.checkReverseSortDirection(column.sortReverseDirection)
    );
  }

  /**
   * event paginator save button
   */
  _eventPaginatorAction(): void {
    if (this.paginatorDisableButton) {
      return environment.guardClause;
    }
    this.eventPaginatorAction.emit();
  }

  /**
   * check to reverse sort direction
   *
   * @return {SortDirectionShared} response
   */
  checkReverseSortDirection(reverse: boolean): SortDirectionShared {
    if (!reverse) {
      return this.sortDirection;
    }
    return this.sortDirection === SortDirectionShared.ASC
      ? SortDirectionShared.DESC
      : this.sortDirection;
  }

  rowEditor(index: number) {
    if (this.editable) {
      return this.tableEditor.rowEditor(index);
    }
    return null;
  }
  /**
   * returns the column key used by ngFor to optimize painting
   *
   * @param  {TableColumn<Data}
   */
  getColumnKey(column: TableColumn<Data, DataFilters, SortIdList>): keyof Data {
    return column.key;
  }
}

export class TableRowEditor<Type> {
  private editingRow = false;
  private endEditingRow = false;
  private rowData: Type;

  constructor(private editor: TableEditor<any, Type, any, any>, private rowIndex: number) {
    /** */
  }

  beginEndEditing() {
    this.endEditingRow = true;
  }

  edit() {
    this.editingRow = true;
    this.endEditingRow = false;
    this.rowData = this.editor?.startEditing(this.index);
  }

  save() {
    this.editingRow = false;
    this.editor?.saveEditing(this.index, this.rowData);
    this.rowData = null;
  }

  cancel() {
    this.editingRow = false;
    this.editor?.cancelEditing(this.index, this.rowData);
    this.rowData = null;
  }

  end() {
    this.endEditingRow = false;
  }

  get editing() {
    return this.editingRow;
  }

  get endEditing() {
    return this.endEditingRow;
  }

  get data() {
    return { ...this.rowData };
  }

  get index() {
    return this.rowIndex;
  }
}

export class TableEditor<FlagsEnum, Type, DataFilters, SortIdList> {
  private rowEditors: TableRowEditor<Type>[] = [];

  constructor(
    private muTable: MuTableComponent<FlagsEnum, Type, DataFilters, SortIdList>,
    private table: Table,
    private tableElement: ElementRef
  ) {
    /** */
  }

  startEditing(index: number) {
    const rowData = { ...this.muTable.dataList.values[index] };
    this.table?.initRowEdit(this.muTable.dataList.values[index]);
    return rowData;
  }

  saveEditing(index: number, data: Type) {
    const element = this.tableElement.nativeElement as HTMLElement;
    const row = element.querySelector(`#row_${index}`);
    this.table?.saveRowEdit(data, row as HTMLTableRowElement);
  }

  cancelEditing(index: number, cancelData: Type) {
    this.table?.cancelRowEdit(this.muTable.dataList.values[index]);
    this.muTable.dataList.values[index] = cancelData;
  }

  rowEditor(index: number) {
    let editor = this.rowEditors[index];
    if (editor) {
      return editor;
    }
    editor = new TableRowEditor(this, index);
    this.rowEditors[index] = editor;
    return editor;
  }
}
